import { useMemo, useState, useEffect, useEffectEvent } from 'react'
import { type ColumnDef, type RowSelectionState } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Power,
  PowerOff,
  Landmark,
  Banknote,
  Wallet,
  FolderOpen,
  CreditCard,
  Eye,
  Download,
  X,
} from 'lucide-react'
import {
  DataTable,
  ColumnVisibilityMenu,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from '@/components/ui/data-table'
import { useTablePreferences } from '@/hooks/useTablePreferences'
import { CopyableCell } from '@/components/ui/copyable-cell'
import { exportToCSV, formatCurrencyForExport, formatDateForExport, formatBooleanForExport } from '@/services/exportService'
import { toast } from 'sonner'
import type { CuentaConSaldo } from '@/services/cuentasService'
import { formatDateOrDash as formatDate } from '@/lib/utils'

interface CuentasTableProps {
  cuentas: CuentaConSaldo[]
  isLoading?: boolean
  onEditar: (cuenta: CuentaConSaldo) => void
  onEliminar: (cuenta: CuentaConSaldo) => void
  onToggleActiva: (cuenta: CuentaConSaldo) => void
  onVerDetalle?: (cuenta: CuentaConSaldo) => void
  onBatchToggleActiva?: (cuentas: CuentaConSaldo[], activa: boolean) => Promise<void>
}

// Formato de moneda
const formatCurrency = (value: number, moneda: string = 'ARS') =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: moneda }).format(value)

function getTipoBadgeVariant(tipo: string | null): 'default' | 'secondary' | 'outline' {
  switch (tipo) {
    case 'Banco':
      return 'default'
    case 'Wallet':
    case 'MercadoPago': // Legacy
      return 'secondary'
    default:
      return 'outline'
  }
}

// Colores por institucion bancaria conocida
function getInstitucionColor(institucion: string | null): string {
  if (!institucion) return 'text-muted-foreground'
  const inst = institucion.toLowerCase()

  // Bancos argentinos con colores representativos
  if (inst.includes('macro')) return 'text-blue-600'
  if (inst.includes('galicia')) return 'text-orange-500'
  if (inst.includes('santander')) return 'text-red-600'
  if (inst.includes('icbc')) return 'text-red-700'
  if (inst.includes('bbva') || inst.includes('frances')) return 'text-blue-700'
  if (inst.includes('hsbc')) return 'text-red-600'
  if (inst.includes('nacion') || inst.includes('bna')) return 'text-sky-600'
  if (inst.includes('provincia') || inst.includes('bapro')) return 'text-green-700'
  if (inst.includes('ciudad')) return 'text-yellow-600'
  if (inst.includes('patagonia')) return 'text-emerald-600'
  if (inst.includes('supervielle')) return 'text-violet-600'
  if (inst.includes('brubank')) return 'text-violet-500'
  if (inst.includes('uala')) return 'text-fuchsia-500'
  if (inst.includes('mercadopago') || inst.includes('mercado pago')) return 'text-sky-500'
  if (inst.includes('naranja')) return 'text-orange-500'

  return 'text-muted-foreground'
}

function getTipoIcon(tipo: string | null, institucion?: string | null) {
  const colorClass = getInstitucionColor(institucion ?? null)
  const iconClass = `h-4 w-4 ${colorClass}`

  switch (tipo) {
    case 'Banco':
      return <Landmark className={iconClass} />
    case 'Efectivo':
      return <Banknote className={iconClass} />
    case 'Wallet':
    case 'MercadoPago': // Legacy
      return <Wallet className={iconClass} />
    default:
      return <FolderOpen className={iconClass} />
  }
}

export function CuentasTable({
  cuentas,
  isLoading,
  onEditar,
  onEliminar,
  onToggleActiva,
  onVerDetalle,
  onBatchToggleActiva,
}: CuentasTableProps) {
  // Estado de selección
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // Limpiar selección cuando cambian las cuentas
  const clearSelection = useEffectEvent(() => {
    setRowSelection({})
  })
  useEffect(() => {
    clearSelection()
  }, [cuentas])

  // Hook de preferencias de tabla con column sizing
  const {
    visibility,
    saveVisibility,
    columnSizing,
    saveColumnSizing,
    resetVisibility,
    resetColumnSizing,
    resetAll,
  } = useTablePreferences('cuentas', {
    defaultVisibility: {
      select: true,
      nombre: true,
      tipo: true,
      institucion: true,
      cbu_cvu_alias: false,
      moneda: true,
      saldo: false,
      total_movimientos: false,
      ultimo_movimiento: false,
      fecha_creacion: false,
      estado: true,
    },
  })

  // Cuentas seleccionadas (buscar por ID, no por índice)
  const selectedCuentas = useMemo(() => {
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((id) => cuentas.find((c) => c.id === id))
      .filter((c): c is CuentaConSaldo => c !== undefined)
  }, [rowSelection, cuentas])

  // Handler para exportar CSV
  const handleExportCSV = () => {
    const dataToExport = selectedCuentas.length > 0 ? selectedCuentas : cuentas

    exportToCSV(
      dataToExport,
      [
        { key: 'nombre', header: 'Nombre' },
        { key: 'tipo', header: 'Tipo' },
        { key: 'institucion', header: 'Institución' },
        { key: 'cbu_cvu_alias', header: 'CBU/CVU/Alias' },
        { key: 'moneda', header: 'Moneda' },
        {
          key: 'saldo_actual',
          header: 'Saldo',
          accessor: (row) => formatCurrencyForExport(row.saldo_actual),
        },
        {
          key: 'total_movimientos',
          header: 'Total Movimientos',
          accessor: (row) => row.total_movimientos?.toString() || '0',
        },
        {
          key: 'ultimo_movimiento',
          header: 'Último Movimiento',
          accessor: (row) => formatDateForExport(row.ultimo_movimiento),
        },
        {
          key: 'created_at',
          header: 'Fecha Creación',
          accessor: (row) => formatDateForExport(row.created_at),
        },
        {
          key: 'activa',
          header: 'Activa',
          accessor: (row) => formatBooleanForExport(row.activa),
        },
      ],
      `cuentas_${new Date().toISOString().split('T')[0]}`
    )

    toast.success(
      selectedCuentas.length > 0
        ? `${selectedCuentas.length} cuentas exportadas`
        : `${cuentas.length} cuentas exportadas`
    )
  }

  // Handler para activar/desactivar batch
  const handleBatchToggle = async (activa: boolean) => {
    if (!onBatchToggleActiva || selectedCuentas.length === 0) return

    try {
      await onBatchToggleActiva(selectedCuentas, activa)
      setRowSelection({})
      toast.success(
        `${selectedCuentas.length} cuentas ${activa ? 'activadas' : 'desactivadas'}`
      )
    } catch {
      toast.error('Error al cambiar estado de las cuentas')
    }
  }

  // Definición de columnas
  const columns = useMemo<ColumnDef<CuentaConSaldo>[]>(
    () => [
      // 0. Checkbox de selección
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Seleccionar todas"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Seleccionar fila"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        size: 40,
        minSize: 40,
        maxSize: 40,
      },
      // 1. Nombre con icono
      {
        id: 'nombre',
        accessorKey: 'nombre',
        header: 'Nombre',
        meta: { label: 'Nombre' },
        size: 200,
        minSize: 150,
        maxSize: 350,
        cell: ({ row }) => {
          const cuenta = row.original
          return (
            <div className={`flex items-center gap-2 ${!cuenta.activa ? 'opacity-60' : ''}`}>
              {getTipoIcon(cuenta.tipo, cuenta.institucion)}
              <span className="font-medium truncate">{cuenta.nombre}</span>
            </div>
          )
        },
      },
      // 2. Tipo
      {
        id: 'tipo',
        accessorKey: 'tipo',
        header: 'Tipo',
        meta: { label: 'Tipo' },
        size: 100,
        minSize: 80,
        maxSize: 130,
        cell: ({ row }) => (
          <Badge variant={getTipoBadgeVariant(row.original.tipo)}>
            {row.original.tipo || 'Sin tipo'}
          </Badge>
        ),
      },
      // 3. Institución
      {
        id: 'institucion',
        accessorKey: 'institucion',
        header: 'Institución',
        meta: { label: 'Institución' },
        size: 150,
        minSize: 100,
        maxSize: 250,
        cell: ({ row }) => (
          <span className="text-muted-foreground truncate block">
            {row.original.institucion || '-'}
          </span>
        ),
      },
      // 4. CBU/CVU/Alias (copyable)
      {
        id: 'cbu_cvu_alias',
        accessorKey: 'cbu_cvu_alias',
        header: 'CBU/CVU/Alias',
        meta: { label: 'CBU/CVU/Alias' },
        size: 220,
        minSize: 180,
        maxSize: 300,
        cell: ({ row }) => {
          const value = row.original.cbu_cvu_alias
          if (!value) return <span className="text-muted-foreground">-</span>
          return <CopyableCell value={value} className="font-mono text-sm" />
        },
      },
      // 5. Moneda
      {
        id: 'moneda',
        accessorKey: 'moneda',
        header: 'Moneda',
        meta: { label: 'Moneda' },
        size: 80,
        minSize: 60,
        maxSize: 100,
        cell: ({ row }) => (
          <span className="text-sm">{row.original.moneda || 'ARS'}</span>
        ),
      },
      // 6. Saldo
      {
        id: 'saldo',
        accessorFn: (row) => row.saldo_actual || 0,
        header: 'Saldo',
        meta: { label: 'Saldo', headerClassName: 'text-right' },
        size: 130,
        minSize: 100,
        maxSize: 180,
        cell: ({ row }) => {
          const saldo = row.original.saldo_actual || 0
          const moneda = row.original.moneda || 'ARS'
          return (
            <span
              className={`block text-right font-mono tabular-nums ${
                saldo >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {formatCurrency(saldo, moneda)}
            </span>
          )
        },
      },
      // 7. Total Movimientos
      {
        id: 'total_movimientos',
        accessorFn: (row) => row.total_movimientos || 0,
        header: 'Movimientos',
        meta: { label: 'Total Movimientos', headerClassName: 'text-right' },
        size: 110,
        minSize: 90,
        maxSize: 140,
        cell: ({ row }) => {
          const total = row.original.total_movimientos || 0
          return (
            <span className="block text-right tabular-nums">
              {total}
            </span>
          )
        },
      },
      // 8. Último Movimiento
      {
        id: 'ultimo_movimiento',
        accessorKey: 'ultimo_movimiento',
        header: 'Último Mov.',
        meta: { label: 'Último Movimiento' },
        size: 110,
        minSize: 90,
        maxSize: 140,
        cell: ({ row }) => (
          <span className="text-sm">{formatDate(row.original.ultimo_movimiento)}</span>
        ),
      },
      // 9. Fecha Creación
      {
        id: 'fecha_creacion',
        accessorKey: 'created_at',
        header: 'Creada',
        meta: { label: 'Fecha Creación' },
        size: 100,
        minSize: 80,
        maxSize: 130,
        cell: ({ row }) => (
          <span className="text-sm">{formatDate(row.original.created_at)}</span>
        ),
      },
      // 10. Estado
      {
        id: 'estado',
        accessorFn: (row) => (row.activa ? 1 : 0),
        header: 'Estado',
        meta: { label: 'Estado' },
        size: 90,
        minSize: 70,
        maxSize: 120,
        cell: ({ row }) =>
          row.original.activa ? (
            <Badge variant="default" className="bg-green-600">
              Activa
            </Badge>
          ) : (
            <Badge variant="secondary">Inactiva</Badge>
          ),
      },
      // 11. Acciones
      {
        id: 'acciones',
        header: '',
        enableHiding: false,
        enableSorting: false,
        enableResizing: false,
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          const cuenta = row.original
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Abrir menú de acciones">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onVerDetalle && (
                    <DropdownMenuItem onClick={() => onVerDetalle(cuenta)}>
                      <Eye className="mr-2 h-4 w-4" />
                      Ver detalle
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => onEditar(cuenta)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onToggleActiva(cuenta)}>
                    {cuenta.activa ? (
                      <>
                        <PowerOff className="mr-2 h-4 w-4" />
                        Desactivar
                      </>
                    ) : (
                      <>
                        <Power className="mr-2 h-4 w-4" />
                        Activar
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onEliminar(cuenta)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [onEditar, onEliminar, onToggleActiva, onVerDetalle]
  )

  // Instancia de tabla TanStack con resizing y selección
  const table = useReactTable({
    data: cuentas,
    columns,
    state: {
      columnVisibility: visibility,
      columnSizing,
      rowSelection,
    },
    onColumnVisibilityChange: saveVisibility,
    onColumnSizingChange: saveColumnSizing,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'nombre', desc: false }],
    },
    getRowId: (row) => row.id,
  })

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Toolbar con menú de columnas y barra de acciones batch */}
      <div className="flex justify-between items-center shrink-0">
        {/* Barra de acciones batch */}
        {selectedCuentas.length > 0 ? (
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-muted/50">
            <span className="text-sm font-medium">
              {selectedCuentas.length} cuenta{selectedCuentas.length > 1 ? 's' : ''} seleccionada{selectedCuentas.length > 1 ? 's' : ''}
            </span>
            <div className="h-4 w-px bg-border" />
            {onBatchToggleActiva && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBatchToggle(false)}
                >
                  <PowerOff className="h-4 w-4 mr-1" />
                  Desactivar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBatchToggle(true)}
                >
                  <Power className="h-4 w-4 mr-1" />
                  Activar
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-1" />
              Exportar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRowSelection({})}
              aria-label="Limpiar selección"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-1" />
              Exportar CSV
            </Button>
          </div>
        )}

        <ColumnVisibilityMenu
          table={table}
          onResetVisibility={resetVisibility}
          onResetColumnSizing={resetColumnSizing}
          onResetAll={resetAll}
        />
      </div>

      {/* Tabla con scroll interno */}
      <div className="flex-1 min-h-0">
        <DataTable
          table={table}
          isLoading={isLoading}
          emptyMessage="No hay cuentas registradas"
          emptyIcon={<CreditCard className="h-6 w-6 text-muted-foreground" />}
          withVerticalDividers
          stickyRightColumns={['acciones']}
          enableColumnResizing
          enableVerticalScroll
        />
      </div>
    </div>
  )
}
